import { all, put, takeLatest, takeEvery, call } from 'redux-saga/effects';
import * as t from '../action_types';
import * as actions from '../action_creators';
import { get_api, post_api, put_api, api_with_method } from '../api_funcs';
import { get_api_url } from '../../utils/urls';

// Every request carries the CMS JWT (cms_token_3) via the `true` token flag on
// the api helpers. Responses are wrapped as { status:'success', data:... } by the
// goserver (utils.SuccessResponseWithData), so payloads live under response.data.data.

const notifyError = (error, fallback) => {
  const reason =
    error && error.response && error.response.data
      ? error.response.data.reason || error.response.data.message
      : null;
  return actions.set_notification_variable(true, 'error', reason || fallback);
};

function* handle401(error) {
  if (error && error.response && error.response.status === 401) {
    yield put(actions.log_out());
  }
}

// ------------------------------------------------------------------ bootstrap
export function* getBootstrap() {
  try {
    yield put(actions.set_support_variable('bootstrap_status', 'loading'));
    const url = get_api_url('/v2/support/bootstrap', false, true);
    const response = yield call(get_api, url, true);
    yield put(actions.set_support_variable('bootstrap', response.data.data));
    yield put(actions.set_support_variable('bootstrap_status', 'success'));
  } catch (error) {
    yield put(notifyError(error, 'Failed to load inbox'));
    yield put(actions.set_support_variable('bootstrap_status', 'fail'));
    yield* handle401(error);
  }
}

// ------------------------------------------------------------- conversations
export function* getConversations(action) {
  try {
    yield put(actions.set_support_variable('conversations_status', 'loading'));
    const p = action.payload || {};
    const params = new URLSearchParams();
    params.set('folder', p.folder || 'all');
    if (p.status) params.set('status', p.status);
    // Unread-only is per-agent and resolved server-side; omitted when off.
    if (p.unread) params.set('unread', '1');
    if (p.sort) params.set('sort', p.sort);
    if (p.limit) params.set('limit', p.limit);
    if (p.offset) params.set('offset', p.offset);
    const url = get_api_url(`/v2/support/conversations?${params.toString()}`, false, true);
    const response = yield call(get_api, url, true);
    const { data } = response.data;
    yield put(actions.set_support_variable('conversations', data.rows || []));
    yield put(actions.set_support_variable('conversations_total', data.total || 0));
    yield put(actions.set_support_variable('conversations_open_count', data.open_count || 0));
    yield put(actions.set_support_variable('conversations_status', 'success'));
  } catch (error) {
    yield put(notifyError(error, 'Failed to load conversations'));
    yield put(actions.set_support_variable('conversations_status', 'fail'));
    yield* handle401(error);
  }
}

export function* getConversation(action) {
  try {
    // `silent` (used by the 30s poll) refreshes the thread without flipping the
    // spinner on, so the open conversation doesn't flicker.
    if (!action.payload.silent) {
      yield put(actions.set_support_variable('active_conversation_status', 'loading'));
    }
    const url = get_api_url(`/v2/support/conversations/${action.payload.uuid}`, false, true);
    const response = yield call(get_api, url, true);
    yield put(actions.set_support_variable('active_conversation', response.data.data));
    yield put(actions.set_support_variable('active_conversation_status', 'success'));
  } catch (error) {
    yield put(notifyError(error, 'Failed to load conversation'));
    yield put(actions.set_support_variable('active_conversation_status', 'fail'));
    yield* handle401(error);
  }
}

// ------------------------------------------------------ create conversation
// POST /v2/support/conversations — agent-initiated outbound email conversation
// (the "New conversation" modal). Response.data.data is the unwrapped
// { conversation, message, sent, error? }. On success we refresh the nav +
// list, open the new thread, and let the caller (modal) close via `onCreated`.
export function* createConversation(action) {
  try {
    yield put(actions.set_support_variable('create_conversation_status', 'loading'));
    yield put(actions.set_support_variable('create_conversation_result', null));
    const { body, refresh } = action.payload;
    const url = get_api_url('/v2/support/conversations', false, true);
    const response = yield call(post_api, url, body, true);
    const result = response.data.data || {};
    yield put(actions.set_support_variable('create_conversation_result', result));
    const conversation = result.conversation || {};
    // The endpoint returns 200 even when the send failed (sent:false) so the
    // conversation still exists — surface a toast but treat create as done.
    if (result.sent === false) {
      yield put(
        actions.set_notification_variable(
          true,
          'error',
          result.error
            ? `Conversation created but email failed to send: ${result.error}`
            : 'Conversation created but email failed to send'
        )
      );
    }
    yield put(actions.set_support_variable('create_conversation_status', 'success'));
    // Refresh nav counts + list, then open the newly created thread.
    yield put(actions.get_support_bootstrap());
    if (refresh) yield put(actions.get_support_conversations(refresh));
    if (conversation.uuid) {
      yield put(actions.get_support_conversation({ uuid: conversation.uuid }));
    }
  } catch (error) {
    yield put(notifyError(error, 'Failed to create conversation'));
    yield put(actions.set_support_variable('create_conversation_status', 'fail'));
    yield* handle401(error);
  }
}

export function* markRead(action) {
  try {
    const url = get_api_url(`/v2/support/conversations/${action.payload.uuid}/read`, false, true);
    yield call(post_api, url, {}, true);
    // Refresh bootstrap counts (unread/mentions badges) after clearing.
    yield put(actions.get_support_bootstrap());
  } catch (error) {
    yield* handle401(error);
  }
}

// ------------------------------------------------------------------- reply
export function* sendReply(action) {
  try {
    yield put(actions.set_support_variable('reply_status', 'loading'));
    yield put(actions.set_support_variable('reply_result', null));
    const { uuid, body, refresh } = action.payload;
    const url = get_api_url(`/v2/support/conversations/${uuid}/reply`, false, true);
    const response = yield call(post_api, url, body, true);
    const result = response.data.data;
    yield put(actions.set_support_variable('reply_result', result));
    // The endpoint returns 200 even when the send failed (sent:false) so the
    // failed message can be surfaced inline — toast on that case.
    if (result && result.sent === false) {
      yield put(
        actions.set_notification_variable(
          true,
          'error',
          result.error ? `Send failed: ${result.error}` : 'Send failed'
        )
      );
      yield put(actions.set_support_variable('reply_status', 'fail'));
    } else {
      yield put(actions.set_support_variable('reply_status', 'success'));
    }
    // Reconcile thread + list from the server regardless (the message row exists).
    yield put(actions.get_support_conversation({ uuid, silent: true }));
    if (refresh) yield put(actions.get_support_conversations(refresh));
    // Replying implicitly reads the thread — clear its unread badge/counts so a
    // conversation the agent just answered doesn't linger as unread. The silent
    // refetch above appends the outbound to the timeline, so the mark-read
    // effect's dedup marker changes and it would fire anyway; this explicit,
    // idempotent dispatch is belt-and-suspenders.
    yield put(actions.mark_support_read({ uuid }));
  } catch (error) {
    yield put(notifyError(error, 'Failed to send reply'));
    yield put(actions.set_support_variable('reply_status', 'fail'));
    yield* handle401(error);
  }
}

// ------------------------------------------------------------------ mutations
export function* patchConversation(action) {
  try {
    yield put(actions.set_support_variable('patch_status', 'loading'));
    const { uuid, patch, refresh } = action.payload;
    const url = get_api_url(`/v2/support/conversations/${uuid}`, false, true);
    yield call(api_with_method, 'patch', url, patch, true);
    yield put(actions.set_support_variable('patch_status', 'success'));
    // Reconcile the optimistic edit from the server.
    yield put(actions.get_support_conversation({ uuid, silent: true }));
    if (refresh) yield put(actions.get_support_conversations(refresh));
    yield put(actions.get_support_bootstrap());
  } catch (error) {
    yield put(notifyError(error, 'Failed to update conversation'));
    yield put(actions.set_support_variable('patch_status', 'fail'));
    // Revert the optimistic edit by re-fetching authoritative state.
    yield put(actions.get_support_conversation({ uuid: action.payload.uuid, silent: true }));
    if (action.payload.refresh) {
      yield put(actions.get_support_conversations(action.payload.refresh));
    }
    yield* handle401(error);
  }
}

export function* replaceTags(action) {
  try {
    yield put(actions.set_support_variable('tags_status', 'loading'));
    const { uuid, tags, refresh } = action.payload;
    const url = get_api_url(`/v2/support/conversations/${uuid}/tags`, false, true);
    yield call(put_api, url, { tags }, true);
    yield put(actions.set_support_variable('tags_status', 'success'));
    yield put(actions.get_support_conversation({ uuid, silent: true }));
    if (refresh) yield put(actions.get_support_conversations(refresh));
  } catch (error) {
    yield put(notifyError(error, 'Failed to update tags'));
    yield put(actions.set_support_variable('tags_status', 'fail'));
    yield put(actions.get_support_conversation({ uuid: action.payload.uuid, silent: true }));
    yield* handle401(error);
  }
}

export function* addNote(action) {
  try {
    yield put(actions.set_support_variable('note_status', 'loading'));
    const { uuid, body, mentions, attachment_ids } = action.payload;
    const url = get_api_url(`/v2/support/conversations/${uuid}/notes`, false, true);
    // Omit the `mentions` key entirely when the composer found no explicit
    // {uuid,name} tags (e.g. the author typed "@Name" as plain text without
    // picking from the autocomplete). The backend treats an explicitly-empty
    // array as "no mentions" and only falls back to parsing @Name tokens out
    // of the body when the key is ABSENT — so sending [] would silently kill
    // that fallback. Sending {body} alone re-enables the name-parse path.
    const payload = mentions && mentions.length > 0 ? { body, mentions } : { body };
    if (attachment_ids && attachment_ids.length > 0) payload.attachment_ids = attachment_ids;
    yield call(post_api, url, payload, true);
    yield put(actions.set_support_variable('note_status', 'success'));
    yield put(actions.get_support_conversation({ uuid, silent: true }));
  } catch (error) {
    yield put(notifyError(error, 'Failed to add note'));
    yield put(actions.set_support_variable('note_status', 'fail'));
    yield* handle401(error);
  }
}

// -------------------------------------------------------------------- views
export function* saveView(action) {
  try {
    yield put(actions.set_support_variable('save_view_status', 'loading'));
    const { method, uuid, body } = action.payload;
    if (method === 'put') {
      const url = get_api_url(`/v2/support/views/${uuid}`, false, true);
      yield call(put_api, url, body, true);
    } else {
      const url = get_api_url('/v2/support/views', false, true);
      yield call(post_api, url, body, true);
    }
    yield put(actions.set_support_variable('save_view_status', 'success'));
    yield put(actions.set_notification_variable(true, 'success', 'View saved'));
    // Refresh nav so the new/updated view appears.
    yield put(actions.get_support_bootstrap());
  } catch (error) {
    yield put(notifyError(error, 'Failed to save view'));
    yield put(actions.set_support_variable('save_view_status', 'fail'));
    yield* handle401(error);
  }
}

export function* deleteView(action) {
  try {
    yield put(actions.set_support_variable('delete_view_status', 'loading'));
    const url = get_api_url(`/v2/support/views/${action.payload.uuid}`, false, true);
    yield call(api_with_method, 'delete', url, {}, true);
    yield put(actions.set_support_variable('delete_view_status', 'success'));
    yield put(actions.get_support_bootstrap());
  } catch (error) {
    yield put(notifyError(error, 'Failed to delete view'));
    yield put(actions.set_support_variable('delete_view_status', 'fail'));
    yield* handle401(error);
  }
}

// -------------------------------------------------------------------- search
export function* search(action) {
  try {
    yield put(actions.set_support_variable('search_status', 'loading'));
    const q = (action.payload && action.payload.q) || '';
    const url = get_api_url(`/v2/support/search?q=${encodeURIComponent(q)}`, false, true);
    const response = yield call(get_api, url, true);
    yield put(actions.set_support_variable('search_results', response.data.data));
    yield put(actions.set_support_variable('search_status', 'success'));
  } catch (error) {
    yield put(actions.set_support_variable('search_status', 'fail'));
    yield* handle401(error);
  }
}

export default function* supportSaga() {
  yield all([
    takeLatest(t.get_support_bootstrap, getBootstrap),
    takeLatest(t.get_support_conversations, getConversations),
    takeLatest(t.get_support_conversation, getConversation),
    takeEvery(t.create_support_conversation, createConversation),
    takeEvery(t.mark_support_read, markRead),
    takeEvery(t.send_support_reply, sendReply),
    takeEvery(t.patch_support_conversation, patchConversation),
    takeEvery(t.replace_support_tags, replaceTags),
    takeEvery(t.add_support_note, addNote),
    takeLatest(t.save_support_view, saveView),
    takeEvery(t.delete_support_view, deleteView),
    takeLatest(t.search_support, search)
  ]);
}
